import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	type Fetcher,
	useFetcher,
	useLocation,
	useNavigation,
} from '@remix-run/react'
import omit from 'lodash.omit'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { getFetcherType } from 'remix-utils/fetcher-type'
import { useHydrated } from 'remix-utils/use-hydrated'
import {
	CART_FORM_REMOVE_ITEM,
	CART_FORM_SET_QUANTITY,
	CART_I18NEXT,
	CART_INPUTS,
	cartRemoveItemSchema,
	cartSetQuantitySchema,
} from '~/routes/($lang).cart/cart.component.tsx'
import {
	PRODUCTS_SLUG_FORM_ADD_TO_CART,
	PRODUCTS_SLUG_INPUTS,
} from '~/routes/($lang).products.$slug/slug.component.tsx'
import { CartContext } from '~/routes/_layout'
import { type App, ROOT_INPUTS } from '~/routes/_layout/root.component.tsx'
import {
	useCartRemoveItemNotify,
	useCartSetQuantityNotify,
} from '~/routes/_layout/use-cart-notify.tsx'

type UseCartItemProps = {
	setApp: React.Dispatch<React.SetStateAction<App>>
	name: string
	orderItemId: string
	quantity: number
	unitPrice: number
	variantId: string
	isOptimistic?: boolean
	slug: string
}

function useCartItem(props: UseCartItemProps) {
	const {
		setApp,
		name,
		orderItemId,
		quantity,
		unitPrice,
		variantId,
		isOptimistic = true,
		slug,
	} = props

	/**
	 * Optimistic
	 */
	const { setContext } = React.useContext(CartContext)!
	const navigation = useNavigation()
	const isHydrated = useHydrated()
	const location = useLocation()

	let optimisticQuantity: number = quantity
	let optimisticPrice: number = unitPrice * quantity
	let optimisticDeleting: boolean = false

	/**
	 * Fetcher
	 */
	const setQuantityFetcher = useFetcher({
		key: CART_FORM_SET_QUANTITY.generateFetcherKey!({ id: orderItemId }),
	})
	const removeItemFetcher = useFetcher({
		key: CART_FORM_REMOVE_ITEM.generateFetcherKey!({ id: orderItemId }),
	})
	const addToCartFetcher = useFetcher({
		key: PRODUCTS_SLUG_FORM_ADD_TO_CART.generateFetcherKey!({ id: slug }),
	})

	const isSubmission = !(
		['done', 'init'].includes(getFetcherType(setQuantityFetcher, navigation)) &&
		['done', 'init'].includes(getFetcherType(removeItemFetcher, navigation)) &&
		['done', 'init'].includes(getFetcherType(addToCartFetcher, navigation))
	)

	const handleSubmission = (fetcher: Fetcher) => {
		const { formData, data } = fetcher
		const optimistic = formData ? Object.fromEntries(formData.entries()) : data

		if (
			typeof optimistic === 'undefined' ||
			['done', 'init'].includes(getFetcherType(fetcher, navigation))
		) {
			return
		}

		switch (optimistic?.[ROOT_INPUTS.subaction]) {
			case CART_FORM_SET_QUANTITY.subaction:
				optimisticQuantity = Number(optimistic[CART_INPUTS.quantity])
				optimisticPrice = unitPrice * optimisticQuantity
				break
			case CART_FORM_REMOVE_ITEM.subaction:
				optimisticDeleting = true
				break
			case PRODUCTS_SLUG_FORM_ADD_TO_CART.subaction:
				optimisticQuantity = Number(
					optimistic[PRODUCTS_SLUG_INPUTS.quantity] || 1,
				)
				optimisticPrice = unitPrice * optimisticQuantity
				break
		}
	}

	/**
	 * Optimistic
	 */
	handleSubmission(setQuantityFetcher)
	handleSubmission(removeItemFetcher)
	handleSubmission(addToCartFetcher)

	React.useEffect(() => {
		setContext((prev) =>
			isSubmission
				? {
						...omit(prev, 'undefined'),
						[variantId]: {
							orderItemId,
							optimisticQuantity,
							optimisticDeleting,
							optimisticPrice,
						},
					}
				: omit(omit(prev, 'undefined'), variantId),
		)
	}, [
		optimisticQuantity,
		optimisticDeleting,
		optimisticPrice,
		isSubmission,
		setContext,
		variantId,
		orderItemId,
	])

	/**
	 * Forms
	 */
  const setQuantityFormValues = {
    [ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
    [ROOT_INPUTS.redirect]: `${location.pathname}`,
  }
	const [setQuantityForm, setQuantityFields] = useForm({
		defaultValue: {
      ...setQuantityFormValues,
			[CART_INPUTS.quantity]: optimisticQuantity,
      [CART_INPUTS.orderItemId]: orderItemId,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
	})
  const increaseQuantityFormValues = {
    [ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
    [ROOT_INPUTS.redirect]: `${location.pathname}`,
  }
	const [increaseQuantityForm, increaseQuantityFields] = useForm({
		defaultValue: {
      ...increaseQuantityFormValues,
			[CART_INPUTS.quantity]: (optimisticQuantity + 1).toFixed(0),
      [CART_INPUTS.orderItemId]: orderItemId,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
	})
  const decreaseQuantityFormValues = {
    [ROOT_INPUTS.subaction]: CART_FORM_SET_QUANTITY.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
    [ROOT_INPUTS.redirect]: `${location.pathname}`,
  }
	const [decreaseQuantityForm, decreaseQuantityFields] = useForm({
		defaultValue: {
			...decreaseQuantityFormValues,
			[CART_INPUTS.quantity]: Math.max(0, optimisticQuantity - 1).toFixed(0),
      [CART_INPUTS.orderItemId]: orderItemId,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartSetQuantitySchema })
		},
		shouldValidate: 'onSubmit',
	})
  const removeItemFormValues = {
    [ROOT_INPUTS.subaction]: CART_FORM_REMOVE_ITEM.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
    [ROOT_INPUTS.redirect]: `${location.pathname}`,
  }
	const [removeItemForm, removeItemFields] = useForm({
		defaultValue: {
			...removeItemFormValues,
      [CART_INPUTS.itemId]: orderItemId,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: cartRemoveItemSchema })
		},
		shouldValidate: 'onSubmit',
	})

	/**
	 * Interaction
	 */
	const { t } = useTranslation(CART_I18NEXT.routeCart)
	const [isChanged, setIsChanged] = React.useState(false)

	React.useEffect(() => {
    if (isOptimistic) {
      setQuantityForm.update({
        name: CART_INPUTS.quantity,
        value: optimisticQuantity,
      })
      increaseQuantityForm.update({
        name: CART_INPUTS.quantity,
        value: (optimisticQuantity + 1).toFixed(0),
      })
      decreaseQuantityForm.update({
        name: CART_INPUTS.quantity,
        value: (optimisticQuantity - 1).toFixed(0),
      })
    }
	}, [optimisticQuantity])

  React.useEffect(() => {
    if (isOptimistic) {
    setQuantityForm.update({
      name: CART_INPUTS.orderItemId,
      value: orderItemId,
    })
    increaseQuantityForm.update({
      name: CART_INPUTS.orderItemId,
      value: orderItemId,
    })
    decreaseQuantityForm.update({
      name: CART_INPUTS.orderItemId,
      value: orderItemId,
    })
    removeItemForm.update({
      name: CART_INPUTS.itemId,
      value: orderItemId,
    })
      }
  }, [orderItemId])

	useCartSetQuantityNotify({ setApp, setQuantityFetcher, t, name, location })
	useCartRemoveItemNotify({ setApp, removeItemFetcher, t, name, location })

	return {
		optimisticQuantity,
		optimisticDeleting,
		optimisticPrice,
		setQuantityFetcher,
		removeItemFetcher,
		setQuantityFormValues,
		setQuantityForm,
		setQuantityFields,
		increaseQuantityFormValues,
		increaseQuantityForm,
		increaseQuantityFields,
		decreaseQuantityForm,
		decreaseQuantityFormValues,
		decreaseQuantityFields,
		removeItemForm,
		removeItemFormValues,
		removeItemFields,
		t,
		location,
		isHydrated,
		isChanged,
		setIsChanged,
		...(!isOptimistic && {
			optimisticQuantity: quantity,
			optimisticDeleting: false,
			optimisticPrice: unitPrice,
		}),
	}
}

export { type UseCartItemProps, useCartItem }
